import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, MessageCircle, ShoppingBag, User, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { prefetchRoutePath } from '../../lib/routeChunkPreloads';

const warm = (path: string) => () => prefetchRoutePath(path);

/** Full-screen flows: avoid stacking tab bar over primary actions (compose, ops, etc.). */
const HIDE_PREFIXES = ['/district88', '/post/new', '/operations', '/warehouse', '/admin', '/sell'];

function tabIsActive(pathname: string, to: string, end?: boolean): boolean {
  const base = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
  if (base) return true;
  if (to === '/connect') {
    return pathname.startsWith('/connect') || pathname.startsWith('/community') || pathname.startsWith('/clips');
  }
  if (to === '/market') return pathname.startsWith('/market');
  if (to === '/messages') return pathname.startsWith('/messages');
  if (to === '/profile') {
    return (
      pathname.startsWith('/profile') ||
      pathname.startsWith('/settings') ||
      /^\/(creator|collector|seller|curator)\//.test(pathname)
    );
  }
  return false;
}

export const MobileBottomNav: React.FC = () => {
  const { pathname } = useLocation();
  const { isLoggedIn } = useAuth();

  if (HIDE_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const items: { to: string; label: string; icon: React.ReactNode; end?: boolean }[] = [
    { to: '/district', label: 'Home', icon: <Home size={22} strokeWidth={2} />, end: true },
    { to: '/connect', label: 'Connect', icon: <Users size={22} strokeWidth={2} /> },
    { to: '/market', label: 'Market', icon: <ShoppingBag size={22} strokeWidth={2} /> },
    { to: '/messages', label: 'Chat', icon: <MessageCircle size={22} strokeWidth={2} /> },
    { to: isLoggedIn ? '/profile' : '/login', label: 'You', icon: <User size={22} strokeWidth={2} /> },
  ];

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-[42] border-t border-white/[0.06] bg-district-black/85 backdrop-blur-md"
      style={{ paddingBottom: 'max(6px, env(safe-area-inset-bottom, 0px))' }}
      aria-label="Main mobile navigation"
    >
      <div className="mx-auto flex h-16 max-w-lg items-stretch justify-between px-1.5 pt-1">
        {items.map(({ to, label, icon, end }) => {
          const active = tabIsActive(pathname, to, end);
          return (
            <NavLink
              key={to + label}
              to={to}
              end={end}
              onMouseDown={warm(to)}
              onTouchStart={warm(to)}
              className={`relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl px-1 py-1 transition-all press-effect ${
                active ? 'text-white' : 'text-zinc-500'
              }`}
            >
              {active ? (
                <span className="absolute top-0.5 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-red-500" aria-hidden />
              ) : null}
              <span
                className={`flex h-8 w-11 items-center justify-center rounded-2xl transition-colors ${
                  active ? 'bg-white/[0.1]' : ''
                }`}
              >
                {icon}
              </span>
              <span className={`max-w-full truncate text-[10px] font-semibold tracking-wide ${active ? 'text-white' : 'text-zinc-500'}`}>
                {label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
